/**
 * FloatingToolbarLeft - Left floating toolbar
 * Contains: Flow name, save status, and knod actions menu
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import PublicIcon from '@mui/icons-material/Public';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider, IconButton } from '@mui/material';
import { AutoSaveStatus } from './AutoSaveStatus';
import { useIsMobile } from '../../hooks/useIsMobile';
import logger from '../../utils/logger';
import './FloatingToolbarLeft.css';

interface FloatingToolbarLeftProps {
  flowName: string;
  onFlowNameChange: (newName: string) => void;
  autoSaveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastSaved: Date | null;
  leftPanelWidth: number;
  isDirty?: boolean;
  onExportWithMedia?: () => void;
  onExportStructure?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onImport?: () => void;
  onPublish?: () => void;
  canPublish?: boolean;
  onBeforeNavigateHome?: (forceSave: boolean) => Promise<void>;
}

export function FloatingToolbarLeft({
  flowName,
  onFlowNameChange,
  autoSaveStatus,
  lastSaved,
  leftPanelWidth,
  isDirty = false,
  onExportWithMedia: _onExportWithMedia,
  onExportStructure,
  onDuplicate: _onDuplicate,
  onDelete: _onDelete,
  onImport,
  onPublish,
  canPublish = true,
  onBeforeNavigateHome,
}: FloatingToolbarLeftProps) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(flowName);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleSave = () => {
    const newName = editValue.trim() || 'Untitled Knod';
    onFlowNameChange(newName);
    setIsEditing(false);
  };

  return (
    <div 
      className="floating-toolbar-left"
      style={{ left: `${leftPanelWidth + 12}px` }}
    >
      {/* Back to dashboard — mobile only */}
      {isMobile && (
        <button
          className="toolbar-left-back-btn"
          // Save still runs, forced, but the route change doesn't wait on it —
          // see the same handler in NodePalette for why awaiting made the
          // control feel dead for the length of a full-graph upsert.
          onClick={() => {
            onBeforeNavigateHome?.(true).catch((error) => {
              logger.error('[FloatingToolbarLeft] Save on navigate-home failed:', error);
            });
            navigate('/');
          }}
          title="Back to Dashboard"
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </button>
      )}

      {/* Knod Name */}
      <div className="floating-toolbar-left-name">
        <EditIcon sx={{ fontSize: 14, color: 'var(--color-text-secondary)' }} />
        {isEditing ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSave();
              } else if (e.key === 'Escape') {
                setIsEditing(false);
                setEditValue(flowName);
              }
            }}
            onBlur={handleSave}
            onFocus={(e) => e.target.select()}
            autoFocus
          />
        ) : (
          <span
            onClick={() => {
              setEditValue(flowName);
              setIsEditing(true);
            }}
            data-empty={!flowName}
            title="Click to edit knod name"
          >
            {flowName || 'Untitled Knod'}
          </span>
        )}
        
        {/* 3-dot menu button */}
        <IconButton
          size="small"
          onClick={handleMenuOpen}
          className="knod-menu-button"
          sx={{
            padding: '2px',
            marginLeft: 'auto',
            color: 'var(--color-text-secondary)',
            '&:hover': {
              color: 'var(--color-text-primary)',
              backgroundColor: 'var(--color-bg-hover)',
            },
          }}
          aria-label="Knod actions"
          aria-controls={menuOpen ? 'knod-actions-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? 'true' : undefined}
        >
          <MoreVertIcon sx={{ fontSize: 16 }} />
        </IconButton>
        
        {/* Dropdown Menu */}
        <Menu
          id="knod-actions-menu"
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                backgroundColor: 'var(--color-bg-node-body)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-lg)',
                minWidth: 220,
                '& .MuiMenuItem-root': {
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-primary)',
                  padding: '8px 16px',
                  '&:hover': {
                    backgroundColor: 'var(--color-bg-hover)',
                  },
                },
                '& .MuiListItemIcon-root': {
                  color: 'var(--color-text-secondary)',
                  minWidth: 32,
                },
                '& .MuiDivider-root': {
                  borderColor: 'var(--color-border)',
                  margin: '4px 0',
                },
              },
            },
          }}
        >
          {/* Import */}
          <MenuItem 
            onClick={() => {
              handleMenuClose();
              onImport?.();
            }}
          >
            <ListItemIcon>
              <UploadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Import Knod</ListItemText>
          </MenuItem>
          
          <Divider />
          
          {/* Export Structure Only */}
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onExportStructure?.();
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Export Knod</ListItemText>
          </MenuItem>

          {/* Publish: MUI Menu can't accept a Fragment as a child (it iterates
              children directly for focus management), so spread an array instead. */}
          {onPublish && [
            <Divider key="publish-divider" />,
            <MenuItem
              key="publish-item"
              disabled={!canPublish}
              onClick={() => {
                handleMenuClose();
                onPublish();
              }}
            >
              <ListItemIcon>
                <PublicIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="Publish…"
                secondary={!canPublish ? 'Save the Knod first' : undefined}
                secondaryTypographyProps={{
                  sx: { fontSize: '0.7rem', color: 'var(--color-text-secondary)' },
                }}
              />
            </MenuItem>,
          ]}
        </Menu>
      </div>

      {/* Auto-save Status */}
      <AutoSaveStatus
        status={autoSaveStatus}
        lastSaved={lastSaved}
        isDirty={isDirty}
      />
    </div>
  );
}
